////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Library Lobby Pod for Taytrom Publishing Co - Container component with logic and state

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { useState, useEffect, FC } from 'react';

// 🟢 First-party components
import LibraryLobbyPea from '../pea/TaytromPublishingCo_Kings_Home_SRC_Library_Lobby_Pea.js';
import { taytromPubHomeFrontendLogger } from '../../../logger/TaytromPublishingCo_Kings_Home_SRC_Logger.js';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// ═══════════════════════════════════════════════════════════════
// DEFINITIONS (D) - TYPE DEFINITIONS & CONSTANTS
// ═══════════════════════════════════════════════════════════════

// D: FSubFSoFSt (Fixed Substance/Source/Structure) - Book status constants
const D_G_DVT_R_IRO_BOOK_STATUS = {
  PUBLISHED: 'published',
  IN_PROGRESS: 'in-progress',
  COMING_SOON: 'coming-soon'
} as const;

// D: FSubFSoFSt (Fixed Substance/Source/Structure) - View mode constants
const D_G_DVT_R_IRO_VIEW_MODES = {
  CATALOG: 'catalog',
  VIEWING: 'viewing'
} as const;

// D: VSubVSoFSt (Variable Sub/Source, Fixed Structure) - Library Book interface
interface INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook {
  id: string;
  title: string;
  client: string;
  description: string;
  pageCount: number;
  publishDate: string;
  status: 'published' | 'in-progress' | 'coming-soon';
  readLink?: string;
}

// D: VSubVSoFSt (Variable Sub/Source, Fixed Structure) - Pod component props
interface INT_STR_IIS_D_G_DVT_R_IRO_PodLibraryLobbyProps {
  className?: string;
}

// D: FSubFSoFSt (Fixed Substance/Source/Structure) - Baseline library books
const D_G_DVT_R_IRO_LIBRARY_BOOKS: INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook[] = [
  {
    id: 'bound-at-runtime',
    title: 'Bound At Runtime',
    client: 'BitsBound (Showcase)',
    description: 'How a Corporate Technology Attorney Discovered the Bridge Between Law and Code—Unlocking the Next Era of B2B Commerce. A comprehensive 700-page analysis proving legal obligations and code functions share identical mathematical structure.',
    pageCount: 700,
    publishDate: 'December 2025',
    status: 'published',
    readLink: '/library/bound-at-runtime'
  },
  {
    id: 'cissp',
    title: 'The TIR{DVT}O CISSP Study Guide',
    client: 'Taytrom Publishing Co. (Self-Published)',
    description: 'Learning Security Architecture Through Universal Pattern Recognition. Revolutionary CISSP preparation guide teaching all 8 domains through the TIR{DVT}O framework—proving pattern recognition accelerates mastery over rote memorization. Security concepts decomposed into Trigger → Input → Requirements{Definitions, Validations, Transformations} → Output operations.',
    pageCount: 500,
    publishDate: 'Q1 2026',
    status: 'published',
    readLink: '/library/cissp'
  }
];

// ═══════════════════════════════════════════════════════════════
// VALIDATIONS (V) - CONSTRAINT CHECKING FUNCTIONS
// ═══════════════════════════════════════════════════════════════

// V: Validate book exists in library
function V_L_DVT_R_IRO_validateBookExists(
  bookId: string,
  books: INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook[]
): boolean {
  return books.some(book => book.id === bookId);
}

// V: Validate book is published (can be read)
function V_L_DVT_R_IRO_validateBookPublished(
  book: INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook
): boolean {
  return book.status === D_G_DVT_R_IRO_BOOK_STATUS.PUBLISHED;
}

// V: Validate HTML file path format
function V_L_DVT_R_IRO_validateHtmlPath(htmlPath: string): boolean {
  return /^\/library\/[a-z0-9-]+$/.test(htmlPath) || /^\/src\/library\/.*\.html$/.test(htmlPath);
}

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Library Lobby Pod Component
const Pod_Library_Lobby: FC<INT_STR_IIS_D_G_DVT_R_IRO_PodLibraryLobbyProps> = ({ className }) => {
  // VI. DECLARATIONS [PURPLE #FF00FF] - REQUIREMENTS SETUP
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [isLoading, _setIsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<typeof D_G_DVT_R_IRO_VIEW_MODES[keyof typeof D_G_DVT_R_IRO_VIEW_MODES]>(D_G_DVT_R_IRO_VIEW_MODES.CATALOG);
  const [viewingBookId, setViewingBookId] = useState<string | null>(null);
  const [bookHtmlUrl, setBookHtmlUrl] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'coming-soon'>('all');
  const [previewBookId, setPreviewBookId] = useState<string | null>(null);

  // VII. INPUT DECLARATIONS [CYAN #00FFFF]
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: COMPONENT MOUNT & URL-BASED BOOK DETECTION
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: Component mount (empty deps array)
    // INPUT: window.location.pathname, D_G_DVT_R_IRO_LIBRARY_BOOKS
    // REQUIREMENTS {DVT}:
    //   D: Book interface, view modes, HTML path pattern
    //   V: Book exists validation, published status validation, URL format validation
    //   T: Parse URL → Find book → Construct HTML path → Update state
    // OUTPUT: View mode set, book viewer displayed (if valid book in URL)
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.info('Library Lobby Pod mounted', {
      totalBooks: D_G_DVT_R_IRO_LIBRARY_BOOKS.length,
      publishedBooks: D_G_DVT_R_IRO_LIBRARY_BOOKS.filter(b => b.status === D_G_DVT_R_IRO_BOOK_STATUS.PUBLISHED).length,
      comingSoonBooks: D_G_DVT_R_IRO_LIBRARY_BOOKS.filter(b => b.status === D_G_DVT_R_IRO_BOOK_STATUS.COMING_SOON).length
    });

    // Detect if viewing a specific book from URL
    const pathname = window.location.pathname;
    const bookIdMatch = pathname.match(/^\/library\/([a-z0-9-]+)$/);

    if (bookIdMatch) {
      const bookId = bookIdMatch[1];

      // V: Validate book exists
      if (!V_L_DVT_R_IRO_validateBookExists(bookId, D_G_DVT_R_IRO_LIBRARY_BOOKS)) {
        taytromPubHomeFrontendLogger.warn('Book not found in URL', { bookId, pathname });
        return;
      }

      const book = D_G_DVT_R_IRO_LIBRARY_BOOKS.find(b => b.id === bookId);

      if (book && V_L_DVT_R_IRO_validateBookPublished(book)) {
        taytromPubHomeFrontendLogger.info('Viewing book from URL', { bookId, pathname });

        // T: Construct path to HTML file
        const htmlPath = `/src/library/sub-levels/first/${bookId}/TaytromPublishingCo_Kings_Home_SRC_Library_SubLevels_First_${bookId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}.html`;

        // V: Validate HTML path format
        if (!V_L_DVT_R_IRO_validateHtmlPath(htmlPath)) {
          taytromPubHomeFrontendLogger.error('Invalid HTML path format', { htmlPath });
          return;
        }

        // T: Update state to viewing mode
        setViewMode(D_G_DVT_R_IRO_VIEW_MODES.VIEWING);
        setViewingBookId(bookId);
        setBookHtmlUrl(htmlPath);
      } else {
        taytromPubHomeFrontendLogger.warn('Book not found or not published', { bookId });
      }
    }

    return () => {
      taytromPubHomeFrontendLogger.debug('Library Lobby Pod unmounted');
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: KEYBOARD NAVIGATION
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User presses key
    // INPUT: KeyboardEvent
    // REQUIREMENTS {DVT}:
    //   D: Key codes (Escape, Enter, ArrowUp, ArrowDown, ArrowLeft, ArrowRight)
    //   V: View mode check, book existence check, selection bounds check
    //   T: Navigate between books, open viewer, close viewer
    // OUTPUT: Updated selection or view mode, screen reader announcement
    // ═══════════════════════════════════════════════════════════════

    const VSubVSoVSt_T_L_DVT_R_IRO_handleKeyDown = (e: KeyboardEvent) => {
      // Escape: Close preview modal if open
      if (e.key === 'Escape' && previewBookId) {
        e.preventDefault();
        taytromPubHomeFrontendLogger.debug('Escape pressed, closing preview modal');
        VSubVSoVSt_T_L_DVT_R_IRO_handlePreviewClose();
        return;
      }

      // Escape: Return to catalog from viewer
      if (e.key === 'Escape' && viewMode === D_G_DVT_R_IRO_VIEW_MODES.VIEWING) {
        e.preventDefault();
        taytromPubHomeFrontendLogger.debug('Escape pressed, returning to catalog');
        setViewMode(D_G_DVT_R_IRO_VIEW_MODES.CATALOG);
        setViewingBookId(null);
        setBookHtmlUrl(null);
        return;
      }

      // Catalog navigation
      if (viewMode === D_G_DVT_R_IRO_VIEW_MODES.CATALOG) {
        const publishedBooks = D_G_DVT_R_IRO_LIBRARY_BOOKS.filter(b => b.status === D_G_DVT_R_IRO_BOOK_STATUS.PUBLISHED);
        const currentIndex = publishedBooks.findIndex(b => b.id === selectedBookId);

        // Arrow Down/Right: Next book
        if ((e.key === 'ArrowDown' || e.key === 'ArrowRight') && currentIndex < publishedBooks.length - 1) {
          e.preventDefault();
          const nextBook = publishedBooks[currentIndex + 1];
          taytromPubHomeFrontendLogger.debug('Navigating to next book', { bookId: nextBook.id });
          VSubVSoVSt_T_L_DVT_R_IRO_handleBookSelection(nextBook.id);
        }

        // Arrow Up/Left: Previous book
        if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft') && currentIndex > 0) {
          e.preventDefault();
          const prevBook = publishedBooks[currentIndex - 1];
          taytromPubHomeFrontendLogger.debug('Navigating to previous book', { bookId: prevBook.id });
          VSubVSoVSt_T_L_DVT_R_IRO_handleBookSelection(prevBook.id);
        }

        // Enter: Read selected book
        if (e.key === 'Enter' && selectedBookId) {
          e.preventDefault();
          taytromPubHomeFrontendLogger.debug('Enter pressed, reading selected book', { bookId: selectedBookId });
          VSubVSoVSt_T_L_DVT_R_IRO_handleReadBook(selectedBookId);
        }
      }
    };

    window.addEventListener('keydown', VSubVSoVSt_T_L_DVT_R_IRO_handleKeyDown);

    return () => {
      window.removeEventListener('keydown', VSubVSoVSt_T_L_DVT_R_IRO_handleKeyDown);
    };
  }, [viewMode, selectedBookId, previewBookId]);

  // VIII. OPERATIONS [ORANGE #FFA500] - REQUIREMENTS PROCESSING
  // ═══════════════════════════════════════════════════════════════
  // TRANSFORMATIONS (T) - STATE CHANGING OPERATIONS
  // ═══════════════════════════════════════════════════════════════

  const VSubVSoVSt_T_L_DVT_R_IRO_handleBookSelection = (bookId: string): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: SELECT BOOK OPERATION
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User clicks on book card
    // INPUT: bookId (string)
    // REQUIREMENTS {DVT}:
    //   D: selectedBookId state variable
    //   V: Book exists (implicit - UI only shows valid books)
    //   T: Update selectedBookId state
    // OUTPUT: State updated, UI re-renders with selection highlight
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.debug('Book selected', { bookId });

    // T: Update selected book state
    setSelectedBookId(bookId);
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handleReadBook = (bookId: string): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: READ BOOK OPERATION
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User clicks "Read Book" button
    // INPUT: bookId (string)
    // REQUIREMENTS {DVT}:
    //   D: Book object, readLink property
    //   V: Book exists, readLink is defined
    //   T: Navigate to book's readLink URL
    // OUTPUT: Browser navigates to book viewer page
    // ═══════════════════════════════════════════════════════════════

    // V: Find book and validate exists
    const book = D_G_DVT_R_IRO_LIBRARY_BOOKS.find(b => b.id === bookId);
    if (!book) {
      taytromPubHomeFrontendLogger.error('Book not found', { bookId });
      return;
    }

    // V: Validate readLink exists
    if (!book.readLink) {
      taytromPubHomeFrontendLogger.warn('Attempted to read book without readLink', { bookId });
      return;
    }

    taytromPubHomeFrontendLogger.info('Reading book', { bookId, readLink: book.readLink });

    // T: Navigate to book viewer
    window.location.href = book.readLink;
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handleBackToHome = (): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: NAVIGATE TO HOMEPAGE
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User clicks "Back to Home" button
    // INPUT: None (user action only)
    // REQUIREMENTS {DVT}:
    //   D: Homepage URL ("/")
    //   V: None required
    //   T: Navigate browser to homepage
    // OUTPUT: Browser navigates to homepage
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.info('Navigating back to homepage');

    // T: Navigate to homepage
    window.location.href = '/';
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handleSearchChange = (query: string): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: UPDATE SEARCH QUERY
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User types in search input
    // INPUT: query (string)
    // REQUIREMENTS {DVT}:
    //   D: searchQuery state variable
    //   V: None (any string valid)
    //   T: Update searchQuery state, filter books
    // OUTPUT: State updated, UI re-renders with filtered books
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.debug('Search query updated', { query });
    setSearchQuery(query);
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handleFilterChange = (status: 'all' | 'published' | 'coming-soon'): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: UPDATE FILTER STATUS
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User selects filter option
    // INPUT: status ('all' | 'published' | 'coming-soon')
    // REQUIREMENTS {DVT}:
    //   D: filterStatus state variable, status options enum
    //   V: Status must be one of three valid values
    //   T: Update filterStatus state, filter books
    // OUTPUT: State updated, UI re-renders with filtered books
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.debug('Filter status updated', { status });
    setFilterStatus(status);
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_getFilteredBooks = (): INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook[] => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: FILTER AND SEARCH BOOKS
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: Search query or filter status changes
    // INPUT: D_G_DVT_R_IRO_LIBRARY_BOOKS, searchQuery, filterStatus
    // REQUIREMENTS {DVT}:
    //   D: Book array, search string, filter enum
    //   V: None (filtering logic handles all cases)
    //   T: Filter by status → Filter by search query → Return results
    // OUTPUT: Array of filtered books
    // ═══════════════════════════════════════════════════════════════

    let filtered = [...D_G_DVT_R_IRO_LIBRARY_BOOKS];

    // T: Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(book => book.status === filterStatus);
    }

    // T: Filter by search query (title, client, description)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(book =>
        book.title.toLowerCase().includes(query) ||
        book.client.toLowerCase().includes(query) ||
        book.description.toLowerCase().includes(query)
      );
    }

    taytromPubHomeFrontendLogger.debug('Books filtered', {
      totalBooks: D_G_DVT_R_IRO_LIBRARY_BOOKS.length,
      filteredCount: filtered.length,
      searchQuery,
      filterStatus
    });

    return filtered;
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handlePreviewOpen = (bookId: string): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: OPEN BOOK PREVIEW MODAL
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User clicks book card or info button
    // INPUT: bookId (string)
    // REQUIREMENTS {DVT}:
    //   D: previewBookId state variable, book data
    //   V: Book exists in library
    //   T: Update previewBookId state, lock body scroll
    // OUTPUT: Modal opens, body scroll disabled
    // ═══════════════════════════════════════════════════════════════

    // V: Validate book exists
    if (!V_L_DVT_R_IRO_validateBookExists(bookId, D_G_DVT_R_IRO_LIBRARY_BOOKS)) {
      taytromPubHomeFrontendLogger.warn('Cannot preview - book not found', { bookId });
      return;
    }

    taytromPubHomeFrontendLogger.debug('Opening book preview', { bookId });

    // T: Open preview modal
    setPreviewBookId(bookId);

    // T: Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';
  };

  const VSubVSoVSt_T_L_DVT_R_IRO_handlePreviewClose = (): void => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: CLOSE BOOK PREVIEW MODAL
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User clicks close button, backdrop, or presses Escape
    // INPUT: None (user action only)
    // REQUIREMENTS {DVT}:
    //   D: previewBookId state variable
    //   V: None required
    //   T: Clear previewBookId state, restore body scroll
    // OUTPUT: Modal closes, body scroll restored
    // ═══════════════════════════════════════════════════════════════

    taytromPubHomeFrontendLogger.debug('Closing book preview');

    // T: Close preview modal
    setPreviewBookId(null);

    // T: Restore body scroll
    document.body.style.overflow = '';
  };

  // IX. PRE-RENDER [RED #FF0000] - REQUIREMENTS ASSEMBLY
  const podState = {
    books: VSubVSoVSt_T_L_DVT_R_IRO_getFilteredBooks(),
    selectedBookId,
    _isLoading: isLoading,
    viewMode,
    viewingBookId,
    bookHtmlUrl,
    searchQuery,
    filterStatus,
    previewBookId,
    onBookSelect: VSubVSoVSt_T_L_DVT_R_IRO_handleBookSelection,
    onReadBook: VSubVSoVSt_T_L_DVT_R_IRO_handleReadBook,
    onBackToHome: VSubVSoVSt_T_L_DVT_R_IRO_handleBackToHome,
    onSearchChange: VSubVSoVSt_T_L_DVT_R_IRO_handleSearchChange,
    onFilterChange: VSubVSoVSt_T_L_DVT_R_IRO_handleFilterChange,
    onPreviewOpen: VSubVSoVSt_T_L_DVT_R_IRO_handlePreviewOpen,
    onPreviewClose: VSubVSoVSt_T_L_DVT_R_IRO_handlePreviewClose
  };

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return <LibraryLobbyPea {...podState} className={className} />;
};

export default Pod_Library_Lobby;
